import {
  Box,
  Center,
  GridItem,
  SimpleGrid,
  Square,
  Text,
} from "@chakra-ui/react";
import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCopyToClipboard, useMeasure } from "react-use";

import { Button } from "adminComponents/atoms/Button";
import { pxToRem } from "adminComponents/utils/pxToRem";
import { useShowToast } from "adminComponents/utils/toast";
import { config } from "links/lib/constants";
import { AnalyticsEvent } from "links/lib/types";
import { getJoinCodePhoneticString } from "links/lib/util";
import { Card } from "sessionComponents/atoms/Card";
import { Icon } from "sessionComponents/atoms/Icon";
import { ProgressBar } from "sessionComponents/atoms/ProgressBar";
import { useBreakpoints } from "sessionComponents/contexts/breakpoints";
import { useSessionAnalytics } from "sharedComponents/contexts/sessionAnalytics";

export interface IJoinSessionCardProps {
  sessionCode: string;
  classroomStudentCount: number;
  classroomStudentsJoinedCount: number;
  guestStudentsJoinedCount: number;
}

export const JoinSessionCard: React.FC<IJoinSessionCardProps> = ({
  sessionCode,
  classroomStudentCount,
  classroomStudentsJoinedCount,
  guestStudentsJoinedCount,
}) => {
  const { match: currentBreakpoints, fontSizeHalf } = useBreakpoints();
  const [, copyToClipboard] = useCopyToClipboard();
  const showToast = useShowToast();
  const { track } = useSessionAnalytics();
  const [codeTextRef, { width: codeTextRefWidth }] =
    useMeasure<HTMLDivElement>();
  const cardContentRef = useRef<HTMLDivElement>(null);
  const cardContentRefWidth =
    cardContentRef.current?.getBoundingClientRect().width;

  const { t } = useTranslation("session", {
    keyPrefix: "joinSessionCard",
    useSuspense: false,
  });

  const codePhonetic = useMemo(() => {
    return getJoinCodePhoneticString(sessionCode);
  }, [sessionCode]);

  const joinUrl = `${config.joinBaseUrl}/${sessionCode.toLocaleLowerCase()}`;

  const onCopyLink = () => {
    copyToClipboard(joinUrl);
    showToast(t("copiedLink"));

    track(AnalyticsEvent.Session_Lobby_CopyJoinLink, {});
  };

  const fontSize = pxToRem(currentBreakpoints.fontSize);
  const guestFontSize = pxToRem(currentBreakpoints.fontSize * 0.75);
  const codePhoneticFontSize = pxToRem(currentBreakpoints.fontSize * 0.8);
  const sessionCodeFontSize = currentBreakpoints.fontSize * 2.5;

  const getDynamicScaleForCode = () => {
    if (!codeTextRefWidth || !cardContentRefWidth) return undefined;
    return codeTextRefWidth > cardContentRefWidth
      ? `scale(${cardContentRefWidth / codeTextRefWidth})`
      : undefined;
  };

  return (
    <Card h="100%" useThickBorder>
      <Square size="100%" ref={cardContentRef}>
        <SimpleGrid columns={1}>
          <GridItem>
            <Center w="full">
              <Text fontSize={fontSize} fontWeight="bold">
                {config.joinHost}
              </Text>
            </Center>
          </GridItem>
          <GridItem>
            <Center w="full">
              <Text
                ref={codeTextRef}
                textStyle="gameDisplay"
                fontSize={pxToRem(sessionCodeFontSize)}
                transform={getDynamicScaleForCode()}
              >
                {sessionCode.toUpperCase()}
              </Text>
            </Center>
          </GridItem>
          <GridItem padding={pxToRem(currentBreakpoints.padding / 2)}>
            <Center>
              <Box onClick={onCopyLink} style={{ cursor: "pointer" }}>
                <Text
                  textAlign="center"
                  fontSize={codePhoneticFontSize}
                  textStyle="gameText"
                  textColor="link.link-blue"
                  sx={{
                    display: "-webkit-box",
                    "-webkit-line-clamp": "3",
                    "-webkit-box-orient": "vertical",
                    overflow: "hidden",
                  }}
                >
                  {codePhonetic}
                </Text>
              </Box>
            </Center>
          </GridItem>
          <GridItem w="100%" maxWidth="100%">
            <Box maxW="100%">
              <ProgressBar
                label={t("studentsJoined", {
                  joinedCount: classroomStudentsJoinedCount,
                  classroomCount: classroomStudentCount,
                })}
                percentValue={
                  (classroomStudentsJoinedCount / classroomStudentCount) * 100
                }
              />
            </Box>
          </GridItem>
          {guestStudentsJoinedCount > 0 && (
            <GridItem paddingTop={pxToRem(currentBreakpoints.padding / 2)}>
              <Text
                textAlign="center"
                fontSize={guestFontSize}
                textStyle="gameTextWeighted"
                textColor="primary.dark-gray"
              >
                {t("guestsJoined", { guestsJoined: guestStudentsJoinedCount })}
              </Text>
            </GridItem>
          )}
          <GridItem
            paddingTop={pxToRem(currentBreakpoints.padding / 2)}
            w="full"
          >
            <Center w="full">
              <Button
                onClick={onCopyLink}
                variant="adminButtonOutlined"
                size="lg"
                leftIcon={
                  <Icon
                    icon="clipboard"
                    iconColor="utility.link"
                    boxSize={pxToRem(currentBreakpoints.iconSize)}
                    marginLeft={pxToRem(fontSizeHalf)}
                  />
                }
              >
                <Text fontWeight="bold" ml={pxToRem(-12)}>
                  {t("copyJoinLink")}
                </Text>
              </Button>
            </Center>
          </GridItem>
        </SimpleGrid>
      </Square>
    </Card>
  );
};
